import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Search, X } from "lucide-react";
import { API, usePageMeta } from "@/lib/site";

const STATUSES = ["new", "contacted", "scheduled", "closed"];

const STATUS_STYLES = {
  new: "border-silver text-silver",
  contacted: "border-fog text-fog",
  scheduled: "border-white bg-white text-ink",
  closed: "border-line text-steel",
};

export default function AdminLeads() {
  usePageMeta("Admin — Leads", "Black Sky Builders internal lead dashboard.");
  const [token, setToken] = useState(() => localStorage.getItem("bsb_admin_token") || "");
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "x-admin-token": t } });
      setLeads(res.data);
      setError("");
      setToken(t);
      localStorage.setItem("bsb_admin_token", t);
    } catch {
      setLeads(null);
      setError("Invalid passcode.");
      localStorage.removeItem("bsb_admin_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token && leads === null) load(token);
  }, [token, leads, load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}`, { status }, { headers: { "x-admin-token": token } });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      setActive((a) => (a && a.id === id ? { ...a, status } : a));
    } catch {
      setError("Could not update status.");
    }
  };

  const filtered = useMemo(() => {
    if (!leads) return [];
    const q = query.toLowerCase();
    return leads.filter((l) => {
      if (filter !== "all" && l.status !== filter) return false;
      if (!q) return true;
      return [l.name, l.email, l.phone, l.city, l.project_type].join(" ").toLowerCase().includes(q);
    });
  }, [leads, query, filter]);

  if (leads === null) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center px-4 pt-24" data-testid="admin-login">
        <div className="w-full max-w-sm border border-line bg-coal p-8">
          <Lock className="h-6 w-6 text-silver" />
          <h1 className="mt-4 font-display text-xl font-bold uppercase tracking-tight">Lead Dashboard</h1>
          <p className="mt-2 text-sm text-fog">Enter the admin passcode to view consultation requests.</p>
          <form
            onSubmit={(e) => { e.preventDefault(); load(input); }}
            className="mt-6"
          >
            <input
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin passcode"
              data-testid="admin-passcode-input"
              className="field"
            />
            {error && <p className="mt-3 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="admin-login-button" className="btn-primary mt-4 w-full">
              {loading ? "Checking…" : "View Leads"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pb-24 pt-28 sm:px-8 lg:px-16" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-fog">Black Sky Builders — Internal</p>
          <h1 className="mt-2 font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">
            Consultation Leads <span className="text-steel">({filtered.length})</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load(token)} data-testid="admin-refresh-button" className="btn-outline !px-4 !py-3">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          <button
            onClick={() => { localStorage.removeItem("bsb_admin_token"); setToken(""); setLeads(null); }}
            data-testid="admin-logout-button"
            className="btn-outline !px-4 !py-3"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-steel" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone, city, project type…"
            data-testid="admin-lead-search-input"
            className="field !pl-11"
          />
        </div>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-lead-filter-select" className="field sm:w-52">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      <div className="mt-8 space-y-4">
        {filtered.length === 0 && (
          <p className="border border-line bg-coal p-8 text-center text-sm text-fog" data-testid="admin-empty-state">
            No leads match your filters yet.
          </p>
        )}
        {filtered.map((lead) => (
          <div key={lead.id} className="border border-line bg-coal p-5 sm:p-6" data-testid={`admin-lead-row-${lead.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <button onClick={() => setActive(lead)} data-testid={`admin-lead-open-${lead.id}`} className="text-left">
                  <p className="font-display text-base font-bold uppercase tracking-wide hover:underline">{lead.name}</p>
                </button>
                <p className="mt-1 text-sm text-fog">
                  {lead.project_type} — {lead.city}
                </p>
                <p className="mt-1 font-mono text-[11px] tracking-wide text-steel">
                  {new Date(lead.created_at).toLocaleString()} · {lead.phone} · {lead.email}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(lead.id, s)}
                    data-testid={`admin-lead-status-button-${s}-${lead.id}`}
                    aria-pressed={lead.status === s}
                    className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${STATUS_STYLES[s]} ${lead.status === s ? "" : "opacity-40 hover:opacity-100"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm" data-testid="admin-lead-detail-modal" onClick={() => setActive(null)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto border border-line bg-coal p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight">{active.name}</h2>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-fog">{active.project_type}</p>
              </div>
              <button onClick={() => setActive(null)} aria-label="Close" data-testid="admin-lead-detail-close" className="border border-line p-2 text-fog hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <dl className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
              {[
                ["Phone", active.phone], ["Email", active.email], ["Address", active.address || "—"], ["City", active.city],
                ["Budget", active.budget_range || "—"], ["Desired start", active.start_timeframe || "—"],
                ["Plans", active.has_plans || "—"], ["Engineering", active.has_engineering || "—"],
                ["Permits started", active.permits_started || "—"], ["Financing interest", active.financing_interest || "—"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel">{k}</dt>
                  <dd className="mt-1 text-sm text-mist">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6">
              <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel">Project description</dt>
              <dd className="mt-2 border border-line bg-ink p-4 text-sm leading-relaxed text-mist">{active.description}</dd>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
